import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Dialog, Listbox, Transition } from "@headlessui/react";

import Loading from "../../Shared/Loading";
import { Option, Question, QuestionGroup, Skill, Questions, Answer } from "../../../Models";
import DropdownQuestionGroup from "../../Shared/Dropdowns/DropdownQuestionGroup";
import {
  ArrowUturnLeftIcon,
  CheckIcon,
  ChevronUpDownIcon,
  EllipsisHorizontalCircleIcon,
  InformationCircleIcon,
  ListBulletIcon,
  PlusCircleIcon,
} from "@heroicons/react/20/solid";
import DropdownQuestionType from "../../Shared/Dropdowns/DropDownQuestionType";
import Skilldropdown from "../Dropdowns/SkillDropdown";
import ReactQuill from "react-quill";
import { formats, toolbar, WorkerUrl } from "../../../helpers/constants";
import CheckboxInput from "../../CheckboxInput";
import { useNotification } from "../../../context/NotificationContext";
import { AddQuestionTime, deleteQuestions, fetchQuestionGroups, getAllFilterQuestions, getQuestionById, getQuestionsByComprehensive, GetQuestionTimes, postQuestion, postQuestionGroup, putAndPostAssessmentOptions, putAssessmentQuestion, UpdateIsObsolete, UpdateQuestionTime, uploadQuestionImage } from "../../../api/assessmentquestion.api";
import { fetchAllSkills, getQuestionSkill, postQuestionSkill, postSkill } from "../../../api/skill.api";
import { getQuestionOptionsById, whichAnswerFollowUp } from "../../../api/assessmentquestion.api";
import useApiToken from "../../../hooks/useApiToken";

type RoleInputType = "options" | "text" | "number";
type RoleOptionMode = "single" | "multiple";
const ROLE_INPUT_TAG_PREFIX = "roleInputType:";
const ROLE_INPUT_MIN_TAG_PREFIX = "roleInputMin:";
const ROLE_INPUT_MAX_TAG_PREFIX = "roleInputMax:";
const ROLE_OPTION_MODE_TAG_PREFIX = "roleOptionMode:";

const getRoleInputTypeFromTags = (rawTags?: string | null): RoleInputType => {
  if (!rawTags) return "options";

  const found = rawTags
    .split(",")
    .map((tag) => tag.trim())
    .find((tag) =>
      tag.toLowerCase().startsWith(ROLE_INPUT_TAG_PREFIX.toLowerCase())
    );

  const value = found
    ?.slice(found.indexOf(":") + 1)
    .trim()
    .toLowerCase();

  if (value === "text" || value === "number" || value === "options") {
    return value;
  }

  return "options";
};

const upsertRoleInputTypeInTags = (
  rawTags: string,
  inputType: RoleInputType
): string => {
  const tagList = (rawTags || "")
    .split(",")
    .map((tag) => tag.trim())
    .filter(
      (tag) =>
        tag.length > 0 &&
        !tag.toLowerCase().startsWith(ROLE_INPUT_TAG_PREFIX.toLowerCase())
    );

  tagList.push(`${ROLE_INPUT_TAG_PREFIX}${inputType}`);
  return tagList.join(", ");
};

const getRoleOptionModeFromTags = (rawTags?: string | null): RoleOptionMode => {
  if (!rawTags) return "single";

  const found = rawTags
    .split(",")
    .map((tag) => tag.trim())
    .find((tag) =>
      tag.toLowerCase().startsWith(ROLE_OPTION_MODE_TAG_PREFIX.toLowerCase())
    );

  const value = found
    ?.slice(found.indexOf(":") + 1)
    .trim()
    .toLowerCase();

  return value === "multiple" ? "multiple" : "single";
};

const upsertRoleOptionModeInTags = (
  rawTags: string,
  mode: RoleOptionMode
): string => {
  const tagList = (rawTags || "")
    .split(",")
    .map((tag) => tag.trim())
    .filter(
      (tag) =>
        tag.length > 0 &&
        !tag.toLowerCase().startsWith(ROLE_OPTION_MODE_TAG_PREFIX.toLowerCase())
    );

  tagList.push(`${ROLE_OPTION_MODE_TAG_PREFIX}${mode}`);
  return tagList.join(", ");
};

const getRoleInputRangeFromTags = (
  rawTags?: string | null
): { lowerLimit: string; upperLimit: string } => {
  if (!rawTags) {
    return { lowerLimit: "", upperLimit: "" };
  }

  const tagList = rawTags.split(",").map((tag) => tag.trim());
  const lowerTag = tagList.find((tag) =>
    tag.toLowerCase().startsWith(ROLE_INPUT_MIN_TAG_PREFIX.toLowerCase())
  );
  const upperTag = tagList.find((tag) =>
    tag.toLowerCase().startsWith(ROLE_INPUT_MAX_TAG_PREFIX.toLowerCase())
  );

  const lowerLimit = lowerTag
    ? lowerTag.slice(lowerTag.indexOf(":") + 1).trim()
    : "";
  const upperLimit = upperTag
    ? upperTag.slice(upperTag.indexOf(":") + 1).trim()
    : "";

  return { lowerLimit, upperLimit };
};

const upsertRoleInputRangeInTags = (
  rawTags: string,
  lowerLimit: string,
  upperLimit: string
): string => {
  const tagList = (rawTags || "")
    .split(",")
    .map((tag) => tag.trim())
    .filter(
      (tag) =>
        tag.length > 0 &&
        !tag.toLowerCase().startsWith(ROLE_INPUT_MIN_TAG_PREFIX.toLowerCase()) &&
        !tag.toLowerCase().startsWith(ROLE_INPUT_MAX_TAG_PREFIX.toLowerCase())
    );

  if (lowerLimit.trim().length > 0) {
    tagList.push(`${ROLE_INPUT_MIN_TAG_PREFIX}${lowerLimit.trim()}`);
  }
  if (upperLimit.trim().length > 0) {
    tagList.push(`${ROLE_INPUT_MAX_TAG_PREFIX}${upperLimit.trim()}`);
  }

  return tagList.join(", ");
};

export default function QuestionAddEditModal({
  size,
  onCancel,
  isModalClosed,
  selectedQuestionId,
}: {
  onCancel: Function;
  isModalClosed: Function;
  size: string;
  selectedQuestionId: string;
}) {
  const token = useApiToken();

  const [followUpOptions, setFollowUpOptions] = useState<[]>([]);
  const [answerFollowUp, setAnswerFollowUp] = useState<string | null>(null);

  const [open, setOpen] = useState(true);

  const [loading, setLoading] = useState<boolean>(true);
  const [questionTimes, setQuestionTimes] = useState<
    {
      id: string;
      time: number;
      accuracy: Number;
      isObsolete: boolean;
      questionDisplayTime?: number;
    }[]
  >([]);
  const [showNewTimeInput, setShowNewTimeInput] = useState(false);
  const [originalQuestionTimes, setOriginalQuestionTimes] = useState<
    {
      id: string;
      time: number;
      accuracy: number;
      isObsolete: boolean;
      questionDisplayTime?: number;
    }[]
  >([]);
  const [newTime, setNewTime] = useState<number | "">("");
  const [newQuestionDisplayTime, setNewQuestionDisplayTime] = useState<
    number | ""
  >("");
  const [editingTimeId, setEditingTimeId] = useState<string | null>(null);
  const [editingTime, setEditingTime] = useState<number | "">("");
  const [editingQuestionDisplayTime, setEditingQuestionDisplayTime] = useState<
    number | ""
  >("");
  const cancelButtonRef = useRef(null);
  useEffect(() => {
    if (open === false) {
      isModalClosed();
    }
  }, [open, isModalClosed]);
  const [selectedQType, setSelectedQType] = useState<Question>({
    id: "1",
    title: "SINGLE",
  });

  const [qGroups, setQGroups] = useState<Question[]>([]);

  const getAllQuestionGroups = useCallback(() => {
    if (!selectedQuestionId) {
      setLoading(false);
    }
    if (!token) return;
    fetchQuestionGroups(token).then((res) => {
      setQGroups(res);
    });
  }, [token, selectedQuestionId]);

  useEffect(() => {
    getAllQuestionGroups();
  }, [getAllQuestionGroups]);

  const [selectedQGroup, setSelectedQGroup] = useState<QuestionGroup>();
  const [options, setOptions] = useState<Option[] | undefined>();
  const [text, setText] = useState("");
  const [time, setTime] = useState("");
  const [tags, setTags] = useState("");
  const [setupScript, setSetupScript] = useState("");
  const [expectedQuery, setExpectedQuery] = useState("");
  const [initialCode, setInitialCode] = useState("");
  const [testCases, setTestCases] = useState('[\n  {\n    "input": "1, 2",\n    "expectedOutput": "3",\n    "isHidden": false\n  }\n]');
  const [fieldName, setFieldName] = useState("");
  const [roleQuestionInputType, setRoleQuestionInputType] =
    useState<RoleInputType>("options");
  const [roleOptionMode, setRoleOptionMode] =
    useState<RoleOptionMode>("single");
  const [roleNumberLowerLimit, setRoleNumberLowerLimit] = useState("");
  const [roleNumberUpperLimit, setRoleNumberUpperLimit] = useState("");
  const [isFollowUpQuestion, setIsFollowUpQuestion] = useState(false);
  const [parentQuestionId, setParentQuestionId] = useState<string | null>(null);
  const [parentQuestionSearch, setParentQuestionSearch] = useState("");
  const [isTesting, setIsTesting] = useState(false);

  const handleValidateCode = async (type: 'REACT' | 'JAVASCRIPT' | 'SQL') => {
    if (type === 'REACT' || type === 'JAVASCRIPT') {
      if (!initialCode.trim() && !expectedQuery.trim()) {
        notifyError("Error", "Please provide Code/Component Template or Correct Solution to test.");
        return;
      }
      if (!testCases.trim()) {
        notifyError("Error", "Please provide Test Cases.");
        return;
      }
    } else if (type === 'SQL') {
      if (!setupScript.trim() || !expectedQuery.trim()) {
        notifyError("Error", "Please provide Setup Script and Expected Query.");
        return;
      }
    }

    setIsTesting(true);
    try {
      const endpoint = type === 'REACT' ? '/run-react' : type === 'JAVASCRIPT' ? '/run-js' : '/run-sql';
      const payload = type === 'SQL' 
        ? { query: expectedQuery, setupScript: setupScript } 
        : { code: expectedQuery.trim() ? expectedQuery : initialCode, testCases };
        
      const res = await fetch(`${WorkerUrl}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        if (type === 'SQL') {
            success("Success", `SQL Executed! Output: ${data.rowCount} row(s) returned.`);
        } else {
            const passCount = data.results?.filter((r: any) => r.passed).length ?? 0;
            const totalCount = data.results?.length ?? 0;
            if (passCount === totalCount && totalCount > 0) {
              success("Success", `${passCount}/${totalCount} Tests Passed!`);
            } else {
              notifyError("Tests Failed", `${passCount}/${totalCount} tests passed. Check logic.`);
            }
        }
      } else {
        notifyError("Validation Error", data.errorMessage || data.error || "Unknown error occurred.");
      }
    } catch (e) {
      notifyError("Error", `Could not reach evaluation worker at ${WorkerUrl}`);
    } finally {
      setIsTesting(false);
    }
  };
  const [parentQuestionOptions, setParentQuestionOptions] = useState<Questions[]>([]);
  const [selectedParentQuestion, setSelectedParentQuestion] = useState<Questions | null>(null);
  const [allQuestions, setAllQuestions] = useState<Questions[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<Skill[]>([]);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [comprehensiveSearch, setComprehensiveSearch] = useState("");
  const [comprehensiveOptions, setComprehensiveOptions] = useState<
    { id: string; text: string }[]
  >([]);
  const [selectedComprehensiveQuestion, setSelectedComprehensiveQuestion] =
    useState<{ id: string; text: string } | null>(null);

  const [linkedQuestions, setLinkedQuestions] = useState<
    { id: string; text: string; type: string }[]
  >([]);
  const [questionImageUrl, setQuestionImageUrl] = useState<string | null>(null);
  const [uploadingQuestionImage, setUploadingQuestionImage] = useState(false);
  const [optionInputMode, setOptionInputMode] = useState<'text' | 'image'>('text');

  useEffect(() => {
    if (!parentQuestionId) {
      setFollowUpOptions([]);
      return;
    }
    const fetchFollowUpOptions = async () => {
      try {
        const followOptions = await getQuestionOptionsById(parentQuestionId);
        setFollowUpOptions(followOptions);
      } catch (error) {
        console.error("Error fetching follow-up options:", error);
        setFollowUpOptions([]);
      }

    };
    fetchFollowUpOptions();
  }, [parentQuestionId, token]);

  useEffect(() => {
    if (!selectedQuestionId) {
      setLoading(false);
      return;
    }

    if (!token) return;

    let cancelled = false;

    const getDetails = async () => {
      setLoading(true);
      try {
        const res = await getQuestionById(selectedQuestionId, token);

        if (cancelled) return;

        setText(res.text);
        setTags(res.tags);
        setSetupScript(res.setupScript || "");
        setExpectedQuery(res.expectedQuery || "");
        setInitialCode(res.initialCode || "");
        setTestCases(res.testCases || '[\n  {\n    "input": "1, 2",\n    "expectedOutput": "3",\n    "isHidden": false\n  }\n]');
        setFieldName(res.fieldName ?? "");
        setTime(res.time);
        setAnswerFollowUp(res?.whichAnswerFollowUp ? String(res.whichAnswerFollowUp) : null);
        setQuestionImageUrl(res.imageUrl ?? null);

        if (res?.parentQuestionId) {
          setIsFollowUpQuestion(true);
          setParentQuestionId(res.parentQuestionId);
          try {
            const parentQ = await getQuestionById(res.parentQuestionId, token);
            if (!cancelled) {
              setSelectedParentQuestion({
                id: parentQ.id,
                text: parentQ.text,
                type: parentQ.type,
                time: parentQ.time,
                tags: parentQ.tags,
                questionGroup: parentQ.questionGroup,
                answers: parentQ.answers,
                candidateAnswers: null,
                accuracy: parentQ.accuracy,
                questionTimes: parentQ.questionTimes,
              } as Questions);
            }
          } catch {
            if (!cancelled) {
              setSelectedParentQuestion(null);
            }
          }
        } else {
          setIsFollowUpQuestion(false);
          setParentQuestionId(null);
          setSelectedParentQuestion(null);
          setAnswerFollowUp(null);
        }

        if (res.comprehensiveQuestionId) {
          try {
            const parent = await getQuestionById(res.comprehensiveQuestionId, token);
            if (!cancelled) {
              setSelectedComprehensiveQuestion({
                id: parent.id,
                text: parent.text,
              });
            }
          } catch {
            if (!cancelled) {
              setSelectedComprehensiveQuestion(null);
            }
          }
        } else {
          setSelectedComprehensiveQuestion(null);
        }

        const qType: string = res.type.toUpperCase();
        const type: Question = {
          id: Math.random().toString(),
          title: qType,
        };
        setSelectedQType(type);
        if (qType === "ROLE QUESTION") {
          setRoleQuestionInputType(getRoleInputTypeFromTags(res.tags));
          setRoleOptionMode(getRoleOptionModeFromTags(res.tags));
          const { lowerLimit, upperLimit } = getRoleInputRangeFromTags(res.tags);
          setRoleNumberLowerLimit(lowerLimit);
          setRoleNumberUpperLimit(upperLimit);
        } else {
          setRoleQuestionInputType("options");
          setRoleOptionMode("single");
          setRoleNumberLowerLimit("");
          setRoleNumberUpperLimit("");
        }

        setOptions(res.answers);
        const group: QuestionGroup = res.questionGroup;
        setSelectedQGroup(group);

        const skills = await getQuestionSkill(selectedQuestionId, token);
        if (!cancelled) {
          setSelectedSkills(skills);
        }

        await getQuestionTimes(selectedQuestionId);
      } catch {
        if (!cancelled) {
          setSelectedSkills([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    getDetails();

    return () => {
      cancelled = true;
    };
  }, [selectedQuestionId, token]);

  const getAllSkills = () => {
    if (!token) return;
    fetchAllSkills(token).then((res) => setSkills(res));
  };
  useEffect(() => {
    getAllSkills();
  }, [token]);

  useEffect(() => {
    if (selectedQType.title !== "ROLE QUESTION") {
      setRoleQuestionInputType("options");
      setRoleOptionMode("single");
      setRoleNumberLowerLimit("");
      setRoleNumberUpperLimit("");
      setFieldName("");
    }
  }, [selectedQType.title]);

  useEffect(() => {
    if (
      !selectedQuestionId ||
      selectedQType.title.toLowerCase() !== "COMPREHENSIVE".toLowerCase()
    )
      return;
    const parentId = selectedQuestionId;
    if (!parentId || !token) {
      setLinkedQuestions([]);
      return;
    }

    let cancelled = false;
    const load = async () => {
      try {
        const res = await getQuestionsByComprehensive(parentId, token);
        if (!cancelled && Array.isArray(res)) {
          const list = res.map((q: any) => ({
            id: q.id,
            text: q.text,
            type: q.type,
          }));
          setLinkedQuestions(list);
        }
      } catch {
        if (!cancelled) {
          setLinkedQuestions([]);
        }
      }
    };
    load();

    return () => {
      cancelled = true;
    };
  }, [selectedQuestionId, selectedQType, token]);

  const stripHtml = (html: string) =>
    html
      ? html
        .replace(/<[^>]+>/g, " ")
        .replace(/\s+/g, " ")
        .trim()
      : "";

  useEffect(() => {
    const shouldShowComprehensiveSelector =
      selectedQType.title !== "COMPREHENSIVE" &&
      selectedQType.title !== "ROLE QUESTION";
    if (!shouldShowComprehensiveSelector) {
      setSelectedComprehensiveQuestion(null);
      setComprehensiveOptions([]);
      return;
    }

    const search = comprehensiveSearch.trim();
    if (!search) {
      setComprehensiveOptions([]);
      return;
    }

    let cancelled = false;
    const handler = setTimeout(async () => {
      try {
        if (!token) return;
        const res = await getAllFilterQuestions(
          token,
          { min: 0, max: 100 },
          [],
          [],
          [],
          ["COMPREHENSIVE"],
          1,
          search,
          []
        );
        if (!cancelled && res?.questions) {
          const list = res.questions.map((q: any) => ({
            id: q.id,
            text: q.text,
          }));
          setComprehensiveOptions(list);
        }
      } catch (e) {
        if (!cancelled) {
          setComprehensiveOptions([]);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(handler);
    };
  }, [comprehensiveSearch, selectedQType.title]);
  const getQuestionTimes = async (id: string) => {
    if (!token) return;
    const res = await GetQuestionTimes(id, token);
    if (res) {
      setQuestionTimes(res);
      setOriginalQuestionTimes(JSON.parse(JSON.stringify(res)));
    }
  };
  const setSelectedQuestionGroup = (selected: QuestionGroup | undefined) => {
    setSelectedQGroup(selected);
  };

  const setSelectedQuestionType = (selected: Question) => {
    setSelectedQType(selected);
  };

  const getOptionsfromChild = (options: Option[]) => {
    setOptions(options);
  };
  const { success, error: notifyError } = useNotification();

  const stripHtmlTags = (html: string) =>
    html
      ? html
        .replace(/<[^>]+>/g, " ")
        .replace(/\s+/g, " ")
        .trim()
      : "";

  // Calculate depth level of a question in the hierarchy
  const getQuestionDepth = (questionId: string, questions: Questions[]): number => {
    let depth = 0;
    let currentId: string | null | undefined = questionId;

    while (currentId) {
      const question = questions.find(q => q.id === currentId);
      if (!question?.parentQuestionId) break;
      currentId = question.parentQuestionId;
      depth++;
    }

    return depth;
  };

  // Search for parent questions (INTERVIEW type only, allow infinite nesting)
  useEffect(() => {
    if (!isFollowUpQuestion) {
      setParentQuestionOptions([]);
      return;
    }

    const search = parentQuestionSearch.trim();
    if (!search) {
      setParentQuestionOptions([]);
      return;
    }

    let cancelled = false;
    const handler = setTimeout(async () => {
      try {
        if (!token) return;
        const res = await getAllFilterQuestions(
          token,
          { min: 0, max: 100 },
          [], // groups
          [], // skills
          [], // assessments
          ["INTERVIEW", "ROLE QUESTION"], // Only INTERVIEW type questions
          1,
          search,
          []
        );
        if (!cancelled && res?.questions) {
          // Filter out only the current question being edited (to prevent self-reference)
          // Allow any other INTERVIEW question as parent (infinite nesting)
          const filtered = res.questions.filter((q: Questions) =>
            q.id !== selectedQuestionId
          );
          setParentQuestionOptions(filtered);
        }
      } catch (e) {
        if (!cancelled) {
          setParentQuestionOptions([]);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(handler);
    };
  }, [parentQuestionSearch, isFollowUpQuestion, selectedQuestionId]);

  const handleSelectParentQuestion = (q: Questions) => {
    setSelectedParentQuestion(q);
    setParentQuestionId(q.id);
    setAnswerFollowUp(null);
    setParentQuestionSearch("");
    setParentQuestionOptions([]);
  };

  const handleClearParentQuestion = () => {
    setSelectedParentQuestion(null);
    setParentQuestionId(null);
    setAnswerFollowUp(null);
    setParentQuestionSearch("");
  };

  const isRoleQuestionType = selectedQType.title === "ROLE QUESTION";
  const roleUsesOptions =
    isRoleQuestionType && roleQuestionInputType === "options";
  const shouldShowComprehensiveSelector =
    selectedQType.title !== "COMPREHENSIVE" &&
    !isRoleQuestionType;
  const shouldShowQuestionTimes = !isRoleQuestionType;
  // Single source of truth: used by both the Update button's enabled state and
  // handleQuestionUpdate's guard, so an enabled button can never be a no-op.
  const hasRequiredQuestionTimes =
    !shouldShowQuestionTimes ||
    questionTimes.length > 0 ||
    selectedQType.title === "IMAGE";
  const shouldShowOptions =
    selectedQType.title === "SINGLE" ||
    selectedQType.title === "MULTIPLE" ||
    selectedQType.title === "IMAGE" ||
    roleUsesOptions;
  const parentQuestionType = selectedParentQuestion?.type?.toUpperCase();
  const parentQuestionHasOptions = !!selectedParentQuestion && (
    parentQuestionType === "SINGLE" ||
    parentQuestionType === "MULTIPLE" ||
    (parentQuestionType === "ROLE QUESTION" &&
      getRoleInputTypeFromTags(selectedParentQuestion.tags) === "options")
  );

  const getValidatedRoleNumberLimits = (): {
    isValid: boolean;
    lowerLimit: string;
    upperLimit: string;
  } => {
    const lowerLimit = roleNumberLowerLimit.trim();
    const upperLimit = roleNumberUpperLimit.trim();

    if (!isRoleQuestionType || roleQuestionInputType !== "number") {
      return { isValid: true, lowerLimit: "", upperLimit: "" };
    }

    if (!lowerLimit || !upperLimit) {
      return { isValid: false, lowerLimit, upperLimit };
    }

    const lower = Number(lowerLimit);
    const upper = Number(upperLimit);

    if (!Number.isFinite(lower) || !Number.isFinite(upper) || lower > upper) {
      return { isValid: false, lowerLimit, upperLimit };
    }

    return { isValid: true, lowerLimit, upperLimit };
  };

  const handleQuestionSubmit = async (e: any) => {
    e.preventDefault();
    if (!token) return;
    const requiresOptions =
      selectedQType.title === "SINGLE" ||
      selectedQType.title === "MULTIPLE" ||
      selectedQType.title === "IMAGE" ||
      roleUsesOptions;
    const selectedIsCorrect = options?.filter((o) => {
      return o.isCorrect === true;
    });

    // For IMAGE type, check if each option has either text or imageUrl; for others, check if text exists
    const optionText = selectedQType.title === "IMAGE"
      ? options?.filter((o) => (!o.text || o.text.trim() === "" || o.text === "<p><br></p>") && (!o.imageUrl || o.imageUrl.trim() === ""))
      : options?.filter((o) => o.text.trim() === "");

    if (requiresOptions && optionText && optionText?.length > 0) {
      notifyError("Error", selectedQType.title === "IMAGE" ? "Each option must have either text or image" : "Option field cannot be empty");
    } else if (
      (selectedQType.title === "MULTIPLE") &&
      selectedIsCorrect &&
      selectedIsCorrect.length <= 1
    ) {
      notifyError("Error", "More than options need to be selected when question type is multiple");
    } else if (
      selectedQType.title === "SINGLE" &&
      selectedIsCorrect &&
      selectedIsCorrect.length < 1
    ) {
      notifyError("Error", "Atleast one option need to be selected when question type is single");
    } else if (
      selectedQType.title === "IMAGE" &&
      selectedIsCorrect &&
      selectedIsCorrect.length < 1
    ) {
      notifyError("Error", "Atleast one option need to be selected when question type is image");
    } else {
      const validatedLimits = getValidatedRoleNumberLimits();
      if (!validatedLimits.isValid) {
        notifyError(
          "Error",
          "For number input type, please provide valid Lower and Upper limits (Lower ≤ Upper)."
        );
        return;
      }

      let normalizedTags = isRoleQuestionType
        ? upsertRoleInputTypeInTags(tags, roleQuestionInputType)
        : tags;

      if (isRoleQuestionType && roleUsesOptions) {
        normalizedTags = upsertRoleOptionModeInTags(normalizedTags, roleOptionMode);
      }

      if (isRoleQuestionType && roleQuestionInputType === "number") {
        normalizedTags = upsertRoleInputRangeInTags(
          normalizedTags,
          validatedLimits.lowerLimit,
          validatedLimits.upperLimit
        );
      } else if (isRoleQuestionType) {
        normalizedTags = upsertRoleInputRangeInTags(normalizedTags, "", "");
      }

      const optionsPayload = requiresOptions
        ? (options as Option[] | undefined)
        : undefined;
      const normalizedFieldName =
        selectedQType.title === "ROLE QUESTION" && fieldName.trim().length > 0
          ? fieldName.trim()
          : null;

      // For IMAGE type, validate that there's either text or image (or both)
      const questionText = text || "Image Question";
      const hasValidQuestion = selectedQType.title === "IMAGE"
        ? (!!text && text !== "<p><br></p>") || !!questionImageUrl
        : (!!text && text !== "<p><br></p>");

      if (
        (optionsPayload ||
          selectedQType.title === "TEXT" ||
          selectedQType.title === "VIDEO" ||
          selectedQType.title === "INTERVIEW" ||
          selectedQType.title === "ROLE QUESTION" ||
          selectedQType.title === "COMPREHENSIVE" ||
          selectedQType.title === "SQL" ||
          selectedQType.title === "JAVASCRIPT" ||
          selectedQType.title === "REACT") &&
        text &&
        hasValidQuestion &&
        (!shouldShowQuestionTimes || questionTimes || selectedQType.title === "IMAGE") &&
        normalizedTags
      ) {
        const question = await postQuestion(
          questionText,
          time,
          normalizedTags,
          normalizedFieldName,
          selectedQGroup?.id ?? null,
          selectedQType.title,
          optionsPayload,
          shouldShowComprehensiveSelector
            ? selectedComprehensiveQuestion?.id ?? null
            : null,
          token,
          parentQuestionId,
          selectedQType.title === "SQL" ? setupScript : null,
          (selectedQType.title === "SQL" || selectedQType.title === "JAVASCRIPT" || selectedQType.title === "REACT") ? expectedQuery : null,
          selectedQType.title === "JAVASCRIPT" || selectedQType.title === "REACT" ? initialCode : null,
          selectedQType.title === "JAVASCRIPT" || selectedQType.title === "REACT" ? testCases : null,
          questionImageUrl
        );
        if (shouldShowQuestionTimes) {
        for (const q of questionTimes) {
          await AddQuestionTime(
            question.id,
            q.time,
            50,
            false,
            token,
            q.questionDisplayTime
          );
          }
        }

        if (selectedSkills.length > 0) {
          postQuestionSkill(question.id, selectedSkills, token);
        }
        success("Success", "Question Added Successfully");
        setTimeout(() => {
          isModalClosed();
        }, 500);
      }
    }
  };

  const isDisabled = !(
    (Array.isArray(options) && options.length > 0 ||
      selectedQType.title === "TEXT" ||
      selectedQType.title === "INTERVIEW" ||
      selectedQType.title === "VIDEO" ||
      selectedQType.title === "COMPREHENSIVE" ||
      selectedQType.title === "SQL" ||
      selectedQType.title === "JAVASCRIPT" ||
      selectedQType.title === "REACT" ||
      (isRoleQuestionType && roleQuestionInputType !== "options")) &&
    (selectedQType.title === "IMAGE"
      ? (text.length > 0 && text != "<p><br></p>") || questionImageUrl
      : (text.length > 0 && text != "<p><br></p>")) &&
    hasRequiredQuestionTimes &&
    tags.length > 0 &&
    (roleQuestionInputType !== "number" || getValidatedRoleNumberLimits().isValid)
  )
  const isParentQuestionMissing = isFollowUpQuestion && !parentQuestionId;

  const handleQuestionUpdate = async (e: any): Promise<void> => {
    e.preventDefault();
    if (!token) return;
    if (isParentQuestionMissing) {
      notifyError("Error", "Please select a parent question for follow-up question.");
      return;
    }
    const selectedType = selectedQType.title;
    const requiresOptions =
      selectedQType.title === "SINGLE" ||
      selectedQType.title === "MULTIPLE" ||
      selectedQType.title === "IMAGE" ||
      roleUsesOptions;
    const selectedIsCorrect = options?.filter((o) => {
      return o.isCorrect === true;
    });

    // For IMAGE type, check if each option has either text or imageUrl; for others, check if text exists
    const optionText = selectedQType.title === "IMAGE"
      ? options?.filter((o) => (!o.text || o.text.trim() === "" || o.text === "<p><br></p>") && (!o.imageUrl || o.imageUrl.trim() === ""))
      : options?.filter((o) => o.text.trim() === "");

    if (requiresOptions && optionText && optionText?.length > 0) {
      notifyError("Error", selectedQType.title === "IMAGE" ? "Each option must have either text or image" : "Option field cannot be empty");
    } else if (
      selectedQType.title === "MULTIPLE" &&
      selectedIsCorrect &&
      selectedIsCorrect.length <= 1
    ) {
      notifyError("Error", "More than options need to be selected when question type is multiple");
    } else if (
      selectedQType.title === "SINGLE" &&
      selectedIsCorrect &&
      selectedIsCorrect.length < 1
    ) {
      notifyError("Error", "Atleast one option need to be selected when question type is single");
    } else if (
      selectedQType.title === "IMAGE" &&
      selectedIsCorrect &&
      selectedIsCorrect.length < 1
    ) {
      notifyError("Error", "Atleast one option need to be selected when question type is image");
    } else {
      const validatedLimits = getValidatedRoleNumberLimits();
      if (!validatedLimits.isValid) {
        notifyError(
          "Error",
          "For number input type, please provide valid Lower and Upper limits (Lower ≤ Upper)."
        );
        return;
      }

      let normalizedTags = isRoleQuestionType
        ? upsertRoleInputTypeInTags(tags, roleQuestionInputType)
        : tags;

      if (isRoleQuestionType && roleUsesOptions) {
        normalizedTags = upsertRoleOptionModeInTags(normalizedTags, roleOptionMode);
      }

      if (isRoleQuestionType && roleQuestionInputType === "number") {
        normalizedTags = upsertRoleInputRangeInTags(
          normalizedTags,
          validatedLimits.lowerLimit,
          validatedLimits.upperLimit
        );
      } else if (isRoleQuestionType) {
        normalizedTags = upsertRoleInputRangeInTags(normalizedTags, "", "");
      }

      if (text && normalizedTags && hasRequiredQuestionTimes) {
        try {
          const normalizedFieldName =
            selectedQType.title === "ROLE QUESTION" && fieldName.trim().length > 0
              ? fieldName.trim()
              : null;
          await putAssessmentQuestion(
            selectedQuestionId,
            text,
            time,
            selectedType,
            normalizedTags,
            normalizedFieldName,
            selectedQGroup,
            shouldShowComprehensiveSelector
              ? selectedComprehensiveQuestion?.id ?? null
              : null,
            token,
            parentQuestionId,
            selectedType === "SQL" ? setupScript : null,
            (selectedType === "SQL" || selectedType === "JAVASCRIPT" || selectedType === "REACT") ? expectedQuery : null,
            selectedType === "JAVASCRIPT" || selectedType === "REACT" ? initialCode : null,
            selectedType === "JAVASCRIPT" || selectedType === "REACT" ? testCases : null,
            questionImageUrl
          );

          if (isFollowUpQuestion) {
            await whichAnswerFollowUp(
              selectedQuestionId,
              token,
              answerFollowUp ?? null
            );
          }

          if (requiresOptions && options) {
            await putAndPostAssessmentOptions(selectedQuestionId, options, token);
          }

          await postQuestionSkill(selectedQuestionId, selectedSkills, token);

          if (shouldShowQuestionTimes && hasQuestionTimesChanged()) {
            await handleQuestionTimeUpdate();
          }

          success("Success", "Question Updated Successfully");
          setTimeout(() => {
            isModalClosed();
          }, 500);
        } catch (error) {
          console.error("putAssessmentQuestion failed", error);
          notifyError("Error", "Unable to update question");
        }
      } else {
        notifyError(
          "Error",
          !text
            ? "Question text cannot be empty."
            : !normalizedTags
              ? "Please add at least one skill tag."
              : "Please add at least one question time before updating."
        );
      }
    }
  };

  const openDialog = (type: string) => {
    setIsDialogOpen((prev) => ({
      ...prev,
      [type]: type === "group" ? !isDialogOpen.group : !isDialogOpen.skill,
    }));
  };

  const closeDialog = () => {
    setIsDialogOpen({ skill: false, group: false });
  };

  const handleCreateGroup = async (e: any) => {
    e.preventDefault();
    if (!token) return;
    try {
      await postQuestionGroup(newGroupName, token).then(() => {
        setNewGroupName("");
        closeDialog();
        success("Success", "Question Group Added");
        getAllQuestionGroups();
      });
    } catch (error) {
      notifyError("Error", "Unable to Add Question Group");
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }
  };

  const clearFields = () => {
    setText("");
    setTags("");
    setFieldName("");
    setOptions([]);
    setTime("");
    setInitialCode("");
    setTestCases('[\n  {\n    "input": "1, 2",\n    "expectedOutput": "3",\n    "isHidden": false\n  }\n]');
    setRoleNumberLowerLimit("");
    setRoleNumberUpperLimit("");
    setQuestionImageUrl(null);
  };

  const handleQuestionImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !token) return;

    setUploadingQuestionImage(true);
    try {
      const imageUrl = await uploadQuestionImage(file, token);
      setQuestionImageUrl(imageUrl);
      success("Success", "Image uploaded successfully");
    } catch (error) {
      notifyError("Error", "Failed to upload image");
    } finally {
      setUploadingQuestionImage(false);
    }
  };

  const handleOptionImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, optionId: number) => {
    const file = e.target.files?.[0];
    if (!file || !token) return;

    try {
      const imageUrl = await uploadQuestionImage(file, token);
      setOptions((prevOptions) =>
        prevOptions?.map((opt) =>
          opt.id === optionId ? { ...opt, imageUrl } : opt
        )
      );
      success("Success", "Option image uploaded successfully");
    } catch (error) {
      notifyError("Error", "Failed to upload option image");
    }
  };

  const onSkillSelect = (currentSelectedSkill: Skill[]) => {
    setSelectedSkills(currentSelectedSkill);
  };

  const handleCreateSkill = async () => {
    if (!token) return;
    await postSkill(newSkillName, token);
    await getAllSkills();
    closeDialog();
    setNewSkillName({
      id: "",
      name: "",
    });
  };
  const [isDialogOpen, setIsDialogOpen] = useState({
    skill: false,
    group: false,
  });
  const [newGroupName, setNewGroupName] = useState<string>("");
  const [newSkillName, setNewSkillName] = useState<Skill>();
  const [pageNumber, setPageNumber] = useState<number>(1);
  useEffect(() => {
    if (pageNumber === 2 && !shouldShowOptions) {
      setPageNumber(1);
    }
  }, [pageNumber, shouldShowOptions]);

  const hasQuestionTimesChanged = (): boolean => {
    return questionTimes.some((qt) => {
      const originalQt = originalQuestionTimes.find((o) => o.id === qt.id);

      if (!originalQt) return true;

      if (
        originalQt.time !== qt.time ||
        originalQt.isObsolete !== qt.isObsolete ||
        originalQt.questionDisplayTime !== qt.questionDisplayTime
      ) {
        return true;
      }

      return false;
    });
  };
  const handleQuestionTimeUpdate = async () => {
    if (!token) return;
    for (const qt of questionTimes) {
      const originalQt = originalQuestionTimes.find((o) => o.id === qt.id);

      if (qt.id.includes("new-")) {
        await AddQuestionTime(
          selectedQuestionId,
          qt.time,
          50,
          qt.isObsolete,
          token,
          qt.questionDisplayTime
        );
      } else if (originalQt) {
        const hasTimeChanged = originalQt.time !== qt.time;
        const hasDisplayTimeChanged =
          originalQt.questionDisplayTime !== qt.questionDisplayTime;
        const hasObsoleteChanged = originalQt.isObsolete !== qt.isObsolete;

        if (hasTimeChanged || hasDisplayTimeChanged) {
          await UpdateQuestionTime(
            selectedQuestionId,
            qt.id,
            qt.time,
            qt.questionDisplayTime,
            token
          );
        }

        if (hasObsoleteChanged) {
          await UpdateIsObsolete(qt.id, qt.isObsolete, token);
        }
      }
    }

    setOriginalQuestionTimes(JSON.parse(JSON.stringify(questionTimes)));
  };
  const toggleIsObsolete = (id: string, checked: boolean) => {
    setQuestionTimes((prev) => {
      return prev.map((item) =>
        item.id === id ? { ...item, isObsolete: checked } : item
      );
    });
  };

  const startEditingTime = (qt: {
    id: string;
    time: number;
    accuracy: Number;
    isObsolete: boolean;
    questionDisplayTime?: number;
  }) => {
    setEditingTimeId(qt.id);
    setEditingTime(qt.time);
    setEditingQuestionDisplayTime(qt.questionDisplayTime ?? "");
  };

  const cancelEditingTime = () => {
    setEditingTimeId(null);
    setEditingTime("");
    setEditingQuestionDisplayTime("");
  };

  const saveEditingTime = (id: string) => {
    if (editingTime && typeof editingTime === "number") {
      setQuestionTimes((prev) => {
        return prev.map((item) =>
          item.id === id
            ? {
              ...item,
              time: editingTime,
              questionDisplayTime:
                editingQuestionDisplayTime !== ""
                  ? editingQuestionDisplayTime
                  : undefined,
            }
            : item
        );
      });
      setEditingTimeId(null);
      setEditingTime("");
      setEditingQuestionDisplayTime("");
    }
  };
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10 dark:bg-entntdarkblue"
        initialFocus={cancelButtonRef}
        onClose={() => {
          onCancel();
          setOpen(false);
        }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-400 bg-opacity-50 dark:bg-opacity-20 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full h-[36rem] flex flex-col dark:bg-darkbg sm:max-w-6xl">
                <div className="bg-gray-200 h-12 p-4 flex items-center dark:bg-darkbglight">
                  <h2 className="font-bold text-gray-600 text-sm text-left dark:text-gray-300">
                    {selectedQuestionId ? "Update Question" : "Create Question"}
                  </h2>
                </div>

                {!loading && (
                  <>
                    <div className="h-[1px] mx-4 my-2 mt-6 w-[80%] self-center rounded-xl dark:bg-white flex bg-gray-600 flex-row justify-around items-center">
                      <div
                        className={`${pageNumber === 1 ? "bg-entntorange" : "bg-gray-700"} p-1 rounded-lg`}
                      >
                        <InformationCircleIcon
                          height={18}
                          color="white"
                          title="Question Info"
                          className="cursor-pointer"
                          onClick={() => setPageNumber(1)}
                        />
                      </div>
                      {shouldShowOptions && (
                        <div
                          className={`${pageNumber === 2 ? "bg-entntorange" : "bg-gray-700"} p-1 rounded-lg`}
                        >
                          <EllipsisHorizontalCircleIcon
                            height={18}
                            color="white"
                            title="Question Options"
                            className="cursor-pointer"
                            onClick={() => setPageNumber(2)}
                          />
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col flex-grow overflow-hidden">
                      <div className="flex-grow overflow-y-auto px-4 sm:px-6 lg:px-8">
                        <form className="mx-auto w-full max-w-full">
                          {pageNumber === 1 && (
                            <div className=" p-6 ">
                              <div className="pb-8 border-b space-y-4">
                                <div className="flex justify-between  border-gray-300 dark:border-gray-600">
                                  <div className="flex items-start flex-col gap-4 lg:flex-row lg:flex-wrap">
                                    <div className="cursor-pointer sm:w-fit lg:w-fit mx-2">
                                      <div className="flex flex-row gap-2 text-xs ">
                                        {!selectedQuestionId && (
                                          <div className="flex flex-row relative marker items-center justify-between mt-4">
                                            {isDialogOpen.group && (
                                              <div className="border border-gray-300 p-2 h-36 flex flex-col justify-between rounded-md absolute z-10 bg-white  dark:bg-darkbg dark:border-gray-600 text-xs lg:left-6 lg:-bottom-3/3 lg:top-0 right-3 top-16 w-fit">
                                                <div>
                                                  <div className=" block sm:text-sm font-medium text-gray-700 w-64 text-left mb-2 dark:text-gray-300 text-sm">
                                                    Create Question Group
                                                  </div>
                                                  <div>
                                                    <input
                                                      type="text"
                                                      className="w-full rounded-md border-gray-300 border p-2 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:border-gray-600 dark:text-gray-400"
                                                      placeholder="Enter Group Name"
                                                      onChange={(e) =>
                                                        setNewGroupName(
                                                          e.target.value
                                                        )
                                                      }
                                                    />
                                                  </div>
                                                </div>
                                                <div className="flex flex-row justify-between">
                                                  <button
                                                    className="text-sm font-semibold text-gray-900 mr-4 bg-gray-300 px-4 py-2 rounded-md"
                                                    onClick={(e) => {
                                                      closeDialog();
                                                      setNewGroupName("");
                                                    }}
                                                  >
                                                    Cancel
                                                  </button>
                                                  <button
                                                    className="bg-indigo-600 text-white hover:bg-indigo-500 cursor-pointer rounded-md px-4 py-2 text-sm font-semibold shadow-sm focus:outline-none focus:ring focus:border-indigo-500 "
                                                    onClick={handleCreateGroup}
                                                  >
                                                    Create{" "}
                                                  </button>
                                                </div>
                                              </div>
                                            )}
                                            <PlusCircleIcon
                                              height={20}
                                              color="blue"
                                              onClick={() =>
                                                openDialog("group")
                                              }
                                              className={`cursor-pointer ${isDialogOpen.group ? "origin-center rotate-45 ease-in duration-100" : ""}`}
                                            />
                                          </div>
                                        )}
                                        <div>
                                          <small className="block sm:text-sm font-medium text-gray-700 w-64 text-left dark:text-gray-400 !text-xs">
                                            Question Group{" "}
                                            <span className="text-red-700">
                                              *
                                            </span>
                                          </small>
                                          <DropdownQuestionGroup
                                            group={qGroups}
                                            setSelectedQuestionGroup={
                                              setSelectedQuestionGroup
                                            }
                                            selectedQGroup={selectedQGroup}
                                          />{" "}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="cursor-pointer sm:w-fit lg:w-fit mx-2  !text-xs">
                                      <small className="block sm:text-sm font-medium text-gray-700 w-64 text-left dark:text-gray-400  !text-xs">
                                        Question Type{" "}
                                        <span className="text-red-700">*</span>
                                      </small>
                                      <div className="flex flex-row gap-2">
                                        <DropdownQuestionType
                                          setSelectedQuestionType={
                                            setSelectedQuestionType
                                          }
                                          selectedQType={selectedQType}
                                        />
                                      </div>
                                    </div>

                                    {selectedQType.title === "ROLE QUESTION" && (
                                      <div className="cursor-pointer sm:w-fit lg:w-fit mx-2 !text-xs">
                                        <small className="block sm:text-sm font-medium text-gray-700 w-64 text-left dark:text-gray-400 !text-xs">
                                          Role Input Type <span className="text-red-700">*</span>
                                        </small>
                                        <div className="w-64">
                                          <Listbox
                                            value={roleQuestionInputType}
                                            onChange={(value) =>
                                              setRoleQuestionInputType(
                                                value as RoleInputType
                                              )
                                            }
                                          >
                                            <div className="relative mt-1">
                                              <Listbox.Button className="relative w-full cursor-default rounded-lg bg-white py-2 pl-3 pr-10 text-left border border-gray-300 focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm dark:bg-transparent dark:border-gray-600">
                                                <span className="block truncate dark:text-gray-300 text-xs capitalize">
                                                  {roleQuestionInputType}
                                                </span>
                                                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                                  <ChevronUpDownIcon
                                                    className="h-5 w-5 text-gray-400"
                                                    aria-hidden="true"
                                                  />
                                                </span>
                                              </Listbox.Button>
                                              <Transition
                                                as={Fragment}
                                                leave="transition ease-in duration-100"
                                                leaveFrom="opacity-100"
                                                leaveTo="opacity-0"
                                              >
                                                <Listbox.Options className="z-10 absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm custom-scroll-small dark:bg-darkbglight">
                                                  {[
                                                    "options",
                                                    "text",
                                                    "number",
                                                  ].map((inputType) => (
                                                    <Listbox.Option
                                                      key={inputType}
                                                      value={inputType}
                                                      className={({ active }) =>
                                                        `cursor-default select-none text-xs py-2 grid grid-cols-5 dark:text-gray-300 hover:bg-blue-200 dark:hover:bg-blue-600 dark:hover:text-white ${active
                                                          ? "bg-blue-100 text-blue-900 dark:text-white dark:bg-blue-600"
                                                          : "text-gray-900"
                                                        }`
                                                      }
                                                    >
                                                      {({ selected }) => (
                                                        <>
                                                          {selected ? (
                                                            <span className="col-span-1 text-xs inset-y-0 left-0 flex items-center pl-3 text-blue-600 text-left dark:text-gray-300">
                                                              <CheckIcon
                                                                className="h-5 w-5"
                                                                aria-hidden="true"
                                                              />
                                                            </span>
                                                          ) : (
                                                            <span className="col-span-1" />
                                                          )}
                                                          <span
                                                            className={`block truncate text-left text-xs col-span-4 capitalize ${selected
                                                              ? "font-medium"
                                                              : "font-normal"
                                                              }`}
                                                          >
                                                            {inputType}
                                                          </span>
                                                        </>
                                                      )}
                                                    </Listbox.Option>
                                                  ))}
                                                </Listbox.Options>
                                              </Transition>
                                            </div>
                                          </Listbox>
                                        </div>
                                      </div>
                                    )}
                                    {selectedQType.title === "ROLE QUESTION" && (
                                      <div className="cursor-pointer sm:w-fit lg:w-fit mx-2 !text-xs mt-2">
                                        <small className="block sm:text-sm font-medium text-gray-700 w-64 text-left dark:text-gray-400 !text-xs">
                                          Field Name (optional)
                                        </small>
                                        <div className="w-64">
                                          <input
                                            type="text"
                                            value={fieldName}
                                            onChange={(e) => setFieldName(e.target.value)}
                                            placeholder="Used as candidate-list header"
                                            className="w-full border border-gray-300 text-sm rounded-lg px-2 py-1 dark:bg-transparent dark:text-gray-200 dark:border-gray-600 placeholder:text-sm"
                                          />
                                        </div>
                                      </div>
                                    )}
                                    {selectedQType.title === "ROLE QUESTION" && roleUsesOptions && (
                                      <div className="cursor-pointer sm:w-fit lg:w-fit mx-2 !text-xs mt-2">
                                        <small className="block sm:text-sm font-medium text-gray-700 w-64 text-left dark:text-gray-400 !text-xs">
                                          Option Selection Mode <span className="text-red-700">*</span>
                                        </small>
                                        <div className="w-64">
                                          <Listbox
                                            value={roleOptionMode}
                                            onChange={(value) =>
                                              setRoleOptionMode(value as RoleOptionMode)
                                            }
                                          >
                                            <div className="relative mt-1">
                                              <Listbox.Button className="relative w-full cursor-default rounded-lg bg-white py-2 pl-3 pr-10 text-left border border-gray-300 focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm dark:bg-transparent dark:border-gray-600">
                                                <span className="block truncate dark:text-gray-300 text-xs capitalize">
                                                  {roleOptionMode}
                                                </span>
                                                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                                  <ChevronUpDownIcon
                                                    className="h-5 w-5 text-gray-400"
                                                    aria-hidden="true"
                                                  />
                                                </span>
                                              </Listbox.Button>
                                              <Transition
                                                as={Fragment}
                                                leave="transition ease-in duration-100"
                                                leaveFrom="opacity-100"
                                                leaveTo="opacity-0"
                                              >
                                                <Listbox.Options className="z-10 absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm custom-scroll-small dark:bg-darkbglight">
                                                  {["single", "multiple"].map((inputType) => (
                                                    <Listbox.Option
                                                      key={inputType}
                                                      value={inputType}
                                                      className={({ active }) =>
                                                        `cursor-default select-none text-xs py-2 grid grid-cols-5 dark:text-gray-300 hover:bg-blue-200 dark:hover:bg-blue-600 dark:hover:text-white ${active
                                                          ? "bg-blue-100 text-blue-900 dark:text-white dark:bg-blue-600"
                                                          : "text-gray-900"
                                                        }`
                                                      }
                                                    >
                                                      {({ selected }) => (
                                                        <>
                                                          {selected ? (
                                                            <span className="col-span-1 text-xs inset-y-0 left-0 flex items-center pl-3 text-blue-600 text-left dark:text-gray-300">
                                                              <CheckIcon
                                                                className="h-5 w-5"
                                                                aria-hidden="true"
                                                              />
                                                            </span>
                                                          ) : (
                                                            <span className="col-span-1" />
                                                          )}
                                                          <span
                                                            className={`block truncate text-left text-xs col-span-4 capitalize ${selected
                                                              ? "font-medium"
                                                              : "font-normal"
                                                              }`}
                                                          >
                                                            {inputType}
                                                          </span>
                                                        </>
                                                      )}
                                                    </Listbox.Option>
                                                  ))}
                                                </Listbox.Options>
                                              </Transition>
                                            </div>
                                          </Listbox>
                                        </div>
                                      </div>
                                    )}
                                    <div className="cursor-pointer sm:w-fit lg:w-fit mx-2 text-xs">
                                      <div className="flex flex-row gap-2">
                                        {!selectedQuestionId && (
                                          <div className="flex mt-4 flex-row relative marker items-center justify-between">
                                            {isDialogOpen.skill && (
                                              <div className="border border-gray-300 p-2 h-36 flex flex-col justify-between rounded-md absolute z-10 bg-white  dark:bg-darkbg dark:border-gray-600 text-xs lg:left-6 lg:-bottom-3/3 lg:top-0 right-3 top-16 w-fit">
                                                <div>
                                                  <div className=" block sm:text-sm font-medium text-gray-700 w-64 text-left mb-2 dark:text-gray-300 text-sm">
                                                    Create Skill{" "}
                                                  </div>
                                                  <div>
                                                    <input
                                                      type="text"
                                                      className="w-full rounded-md border-gray-300 border p-2 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:border-gray-600 dark:text-gray-400"
                                                      placeholder="Enter Skill Name"
                                                      onChange={(e) =>
                                                        setNewSkillName({
                                                          id: "",
                                                          name: e.target.value,
                                                        })
                                                      }
                                                    />
                                                  </div>
                                                </div>
                                                <div className="flex flex-row justify-between">
                                                  <button
                                                    className="text-sm font-semibold text-gray-900 mr-4 bg-gray-300 px-4 py-2 rounded-md"
                                                    onClick={(e) => {
                                                      e.preventDefault();
                                                      closeDialog();
                                                      setNewSkillName({
                                                        id: "",
                                                        name: "",
                                                      });
                                                    }}
                                                  >
                                                    Cancel
                                                  </button>
                                                  <button
                                                    className="bg-indigo-600 text-white hover:bg-indigo-500 cursor-pointer rounded-md px-4 py-2 text-sm font-semibold shadow-sm focus:outline-none focus:ring focus:border-indigo-500 "
                                                    onClick={(e) => {
                                                      e.preventDefault();
                                                      handleCreateSkill();
                                                    }}
                                                  >
                                                    Create
                                                  </button>
                                                </div>
                                              </div>
                                            )}
                                            <PlusCircleIcon
                                              height={20}
                                              color="blue"
                                              onClick={() =>
                                                openDialog("skill")
                                              }
                                              className={`cursor-pointer ${isDialogOpen.skill ? "origin-center rotate-45 ease-in duration-100" : ""}`}
                                            />
                                          </div>
                                        )}
                                        <div>
                                          <small className="block sm:text-sm font-medium text-gray-700 w-full text-left dark:text-gray-400  !text-xs">
                                            Skill Type
                                          </small>
                                          <Skilldropdown
                                            skills={skills}
                                            selectedSkills={selectedSkills}
                                            onSkillsSelect={onSkillSelect}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                    {selectedQType.title === "ROLE QUESTION" &&
                                      roleQuestionInputType === "number" && (
                                        <div className="cursor-pointer mx-6 sm:w-fit lg:w-fit !text-xs">
                                          <small className="block sm:text-sm font-medium text-gray-700 w-64 text-left dark:text-gray-400 !text-xs">
                                            Lower Limit <span className="text-red-700">*</span>
                                          </small>
                                          <div className="w-64">
                                            <div className="relative mt-1">
                                              <input
                                                type="number"
                                                value={roleNumberLowerLimit}
                                                onChange={(e) =>
                                                  setRoleNumberLowerLimit(
                                                    e.target.value
                                                  )
                                                }
                                                className="relative w-full cursor-default rounded-lg bg-white py-2 pl-3 pr-3 text-left border border-gray-300 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:border-gray-600 dark:text-gray-400 text-xs"
                                                placeholder="e.g. 0"
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      )}

                                    {selectedQType.title === "ROLE QUESTION" &&
                                      roleQuestionInputType === "number" && (
                                        <div className="cursor-pointer sm:w-fit lg:w-fit !text-xs">
                                          <small className="block sm:text-sm font-medium text-gray-700 w-64 text-left dark:text-gray-400 !text-xs">
                                            Upper Limit <span className="text-red-700">*</span>
                                          </small>
                                          <div className="w-64">
                                            <div className="relative mt-1">
                                              <input
                                                type="number"
                                                value={roleNumberUpperLimit}
                                                onChange={(e) =>
                                                  setRoleNumberUpperLimit(
                                                    e.target.value
                                                  )
                                                }
                                                className="relative w-full cursor-default rounded-lg bg-white py-2 pl-3 pr-3 text-left border border-gray-300 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:border-gray-600 dark:text-gray-400 text-xs"
                                                placeholder="e.g. 100"
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                  </div>
                                </div>
                                {shouldShowComprehensiveSelector && (
                                  <div className="cursor-pointer sm:w-fit lg:w-fit mx-2 text-xs">
                                    <small className="block sm:text-sm font-medium text-gray-700 w-64 text-left dark:text-gray-400 !text-xs">
                                      Comprehensive Question
                                    </small>
                                    <div className="relative">
                                      <input
                                        type="text"
                                        className="min-w-[26rem] rounded-md border-gray-300 border p-1 pr-8 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600 text-xs"
                                        placeholder="Search comprehensive question"
                                        value={
                                          selectedComprehensiveQuestion?.text ??
                                          comprehensiveSearch
                                        }
                                        onChange={(e) => {
                                          setSelectedComprehensiveQuestion(
                                            null
                                          );
                                          setComprehensiveSearch(
                                            e.target.value
                                          );
                                        }}
                                      />
                                      {comprehensiveSearch.trim() &&
                                        comprehensiveOptions.length > 0 && (
                                          <ul className="absolute z-20 mt-1 max-h-40 w-full overflow-auto rounded-md border border-gray-300 bg-white text-xs shadow-lg dark:bg-darkbg dark:border-gray-600">
                                            {comprehensiveOptions
                                              .filter((q) =>
                                                (comprehensiveSearch || "")
                                                  .toLowerCase()
                                                  .split(" ")
                                                  .every((part) =>
                                                    q.text
                                                      .toLowerCase()
                                                      .includes(part)
                                                  )
                                              )
                                              .slice(0, 20)
                                              .map((q) => (
                                                <li
                                                  key={q.id}
                                                  className="cursor-pointer px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-700"
                                                  onClick={() => {
                                                    setSelectedComprehensiveQuestion(
                                                      q
                                                    );
                                                    setComprehensiveSearch("");
                                                  }}
                                                  title={stripHtml(q.text)}
                                                >
                                                  <span
                                                    className="line-clamp-2"
                                                    dangerouslySetInnerHTML={{
                                                      __html: q.text,
                                                    }}
                                                  />
                                                </li>
                                              ))}
                                          </ul>
                                        )}
                                    </div>
                                  </div>
                                )}

                                {linkedQuestions.length > 0 && (
                                  <div className="mt-4 mx-2 text-xs">
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="font-semibold text-gray-700 dark:text-gray-300">
                                        Questions for this passage
                                      </span>
                                    </div>
                                    <ul className="max-h-32 overflow-auto rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-darkbg">
                                      {linkedQuestions.map((q) => (
                                        <li
                                          key={q.id}
                                          className="flex items-center justify-between px-2 py-1 border-b last:border-b-0 border-gray-100 dark:border-gray-700"
                                        >
                                          <div className="flex-1 mr-2">
                                            <span className="text-[11px] uppercase text-gray-500 dark:text-gray-400 mr-2">
                                              {q.type}
                                            </span>
                                            <span
                                              className="text-xs line-clamp-1 dark:text-gray-200"
                                              dangerouslySetInnerHTML={{
                                                __html: q.text,
                                              }}
                                            />
                                          </div>
                                          <div className="flex gap-1">
                                            <button
                                              type="button"
                                              onClick={() =>
                                                window.open(
                                                  `/questions/edit/${q.id}`,
                                                  "_blank"
                                                )
                                              }
                                              className="px-2 py-[2px] rounded-md text-[11px] bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                                            >
                                              Edit
                                            </button>
                                            <button
                                              type="button"
                                              onClick={async () => {
                                                if (!token) return;
                                                await deleteQuestions([q.id], token);
                                                const parentId =
                                                  selectedComprehensiveQuestion?.id;
                                                if (!parentId) return;
                                                try {
                                                  const res =
                                                    await getQuestionsByComprehensive(
                                                      parentId,
                                                      token
                                                    );
                                                  if (Array.isArray(res)) {
                                                    const list = res.map(
                                                      (item: any) => ({
                                                        id: item.id,
                                                        text: item.text,
                                                        type: item.type,
                                                      })
                                                    );
                                                    setLinkedQuestions(list);
                                                  }
                                                } catch {
                                                  setLinkedQuestions([]);
                                                }
                                              }}
                                              className="px-2 py-[2px] rounded-md text-[11px] bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/40 dark:text-red-300"
                                            >
                                              Delete
                                            </button>
                                          </div>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>

                              {selectedQType.title !== "IMAGE" && (
                                <div className="flex flex-col items-start pt-8 py-2 lg:flex-row ">
                                  <label
                                    htmlFor="questionText"
                                    className=" text-xs block  font-medium text-gray-700 w-64 text-left py-4 dark:text-gray-400"
                                  >
                                    Question Text{" "}
                                    <span className="text-red-700">*</span>
                                  </label>

                                  <ReactQuill
                                    value={text}
                                    onChange={setText}
                                    formats={formats}
                                    modules={{
                                      toolbar: {
                                        container: toolbar,
                                      },
                                    }}
                                    className="w-full rounded-md p-1  focus:outline-none focus:ring focus:border-indigo-500 h-fit text-left dark:text-gray-400 !dark:border-gray-800"
                                    id="questionText"
                                  />
                                </div>
                              )}

                              {selectedQType.title === "IMAGE" && (
                                <div className="flex flex-col items-start py-4 border-b border-gray-300 lg:flex-row dark:border-gray-600">
                                  <label className="text-xs block font-medium text-gray-700 w-64 text-left py-4 dark:text-gray-400">
                                    Question <span className="text-red-700">*</span>
                                  </label>
                                  <div className="w-full">
                                    {/* Text Editor */}
                                    <ReactQuill
                                      value={text}
                                      onChange={setText}
                                      formats={formats}
                                      modules={{
                                        toolbar: {
                                          container: toolbar,
                                        },
                                      }}
                                      className="w-full rounded-md p-1 focus:outline-none focus:ring focus:border-indigo-500 h-fit text-left dark:text-gray-400 !dark:border-gray-800"
                                    />

                                    {/* Image Upload (Optional) */}
                                    <div className="mt-4">
                                      <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-400">
                                        Question Image (Optional)
                                      </label>
                                      <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleQuestionImageUpload}
                                        disabled={uploadingQuestionImage}
                                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 dark:text-gray-400 dark:file:bg-indigo-900 dark:file:text-indigo-200"
                                      />
                                      {uploadingQuestionImage && (
                                        <p className="text-xs text-gray-500 mt-2 dark:text-gray-400">Uploading...</p>
                                      )}
                                      {questionImageUrl && (
                                        <div className="mt-2">
                                          <img src={questionImageUrl} alt="Question" className="max-w-md rounded border border-gray-300 dark:border-gray-600" />
                                          <button
                                            type="button"
                                            onClick={() => setQuestionImageUrl(null)}
                                            className="mt-2 text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                          >
                                            Remove Image
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}


                              {
                                selectedQType.title === "INTERVIEW" || selectedQType.title === 'ROLE QUESTION' && (
                                  <div className=" items-start py-6 border-b border-gray-300 lg:flex-row dark:border-gray-600">
                                    <div className="flex flex-row gap-2 items-center">
                                      <input
                                        type="checkbox"
                                        checked={isFollowUpQuestion}
                                        onChange={(e) => {
                                          setIsFollowUpQuestion(e.target.checked);
                                          if (!e.target.checked) {
                                            handleClearParentQuestion();
                                          }
                                        }}
                                      />
                                      <p className={`block text-sm font-medium text-gray-700 py-4 dark:bg-transparent dark:text-gray-400 ${!isFollowUpQuestion && 'line-through'}`}>
                                        is a followup question?
                                      </p>
                                    </div>
                                    {isFollowUpQuestion && (
                                      <div className="cursor-pointer sm:w-fit lg:w-fit mx-2 text-xs">
                                        <small className="block sm:text-sm font-medium text-gray-700 w-64 text-left dark:text-gray-400 !text-xs">
                                          Parent Question
                                        </small>
                                        <div className="relative">
                                          {selectedParentQuestion ? (
                                            <div className="flex items-center gap-2 min-w-[26rem] rounded-md border-gray-300 border p-2 bg-gray-50 dark:bg-gray-800 dark:border-gray-600">
                                              <span
                                                className="flex-1 text-xs text-gray-700 dark:text-gray-300 line-clamp-2"
                                                dangerouslySetInnerHTML={{ __html: selectedParentQuestion.text }}
                                              />
                                              <button
                                                type="button"
                                                onClick={handleClearParentQuestion}
                                                className="text-red-500 hover:text-red-700 text-sm font-bold"
                                              >
                                                ✕
                                              </button>

                                            </div>
                                          ) : (
                                            <>
                                              <input
                                                type="text"
                                                className="min-w-[26rem] rounded-md border-gray-300 border p-2 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600 text-xs"
                                                placeholder="Search interview and role questions..."
                                                value={parentQuestionSearch}
                                                onChange={(e) => setParentQuestionSearch(e.target.value)}
                                              />
                                              {parentQuestionSearch.trim() && parentQuestionOptions.length > 0 && (
                                                <ul className="absolute z-20 mt-1 max-h-48 w-full overflow-auto rounded-md border border-gray-300 bg-white text-xs shadow-lg dark:bg-darkbg dark:border-gray-600">
                                                  {parentQuestionOptions.slice(0, 15).map((q) => {
                                                    const depth = getQuestionDepth(q.id, parentQuestionOptions);
                                                    return (
                                                      <li
                                                        key={q.id}
                                                        className="cursor-pointer px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                                                        onClick={() => handleSelectParentQuestion(q)}
                                                        title={stripHtmlTags(q.text)}
                                                      >
                                                        <div className="flex items-center gap-2">
                                                          {depth > 0 && (
                                                            <span className="px-1.5 py-0.5 text-[10px] rounded bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300 font-medium shrink-0">
                                                              L{depth}
                                                            </span>
                                                          )}
                                                          <span
                                                            className="line-clamp-2 dark:text-gray-300 flex-1"
                                                            dangerouslySetInnerHTML={{ __html: q.text }}
                                                          />
                                                        </div>
                                                        {q.tags && (
                                                          <span className="text-[10px] text-gray-500 dark:text-gray-400 mt-1 block">
                                                            {q.tags}
                                                          </span>
                                                        )}
                                                      </li>
                                                    );
                                                  })}
                                                </ul>
                                              )}
                                              {parentQuestionSearch.trim() && parentQuestionOptions.length === 0 && (
                                                <div className="absolute z-20 mt-1 w-full rounded-md border border-gray-300 bg-white text-xs shadow-lg dark:bg-darkbg dark:border-gray-600 p-3 text-gray-500 dark:text-gray-400">
                                                  No interview/role questions found
                                                </div>
                                              )}
                                              {isParentQuestionMissing && (
                                                <small className="mt-1 block text-red-600 dark:text-red-400">
                                                  Please select a parent question.
                                                </small>
                                              )}
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    {
                                      isFollowUpQuestion && selectedQType.title === "ROLE QUESTION" && parentQuestionHasOptions && (
                                        <div className="mt-4 p-2">
                                          <div className="text-sm font-medium text-gray-700 dark:text-gray-400 mb-2">
                                            <small>{`Follow-up options for "${stripHtmlTags(selectedParentQuestion.text)}"`}</small>
                                          </div>
                                          <select
                                            className="min-w-[26rem] rounded-md border-gray-300 border p-2 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600 text-xs"
                                            value={answerFollowUp || ""}
                                            onChange={(e) => setAnswerFollowUp(e.target.value)}
                                          >
                                            {!answerFollowUp && (
                                              <option disabled value="">Select a follow-up option</option>
                                            )}
                                            {followUpOptions && followUpOptions.length > 0 ? (
                                              followUpOptions.map((option: any, index: number) => (
                                                <option key={index} value={option.id ? String(option.id) : String(index)}>
                                                  {stripHtmlTags(option.text || option)}
                                                </option>
                                              ))
                                            ) : (
                                              <option disabled>Select a Parent Question First</option>
                                            )}
                                          </select>
                                        </div>
                                      )
                                    }
                                  </div>
                                )
                              }

                              {selectedQType.title === "ROLE QUESTION" &&
                                roleQuestionInputType !== "options" && (
                                  <div className="mx-2 mt-2 text-xs text-gray-500 dark:text-gray-400">
                                    Candidate will answer this role question using a {roleQuestionInputType} input.
                                  </div>
                                )}

                              {shouldShowQuestionTimes && (
                                <div className="flex flex-col items-start py-14 border-b border-gray-300 lg:flex-row dark:border-gray-600 ">
                                  <div className="w-full">
                                    <label className="block text-sm font-medium text-gray-700 py-4 dark:bg-transparent dark:text-gray-400">
                                      Question Times
                                    </label>

                                    <ul className="space-y-2">
                                      {questionTimes.map((qt) => (
                                        <li
                                          key={qt.id}
                                          className="flex items-center gap-2"
                                        >
                                          <input
                                            type="checkbox"
                                            checked={qt.isObsolete}
                                            onChange={(e) => {
                                              toggleIsObsolete(
                                                qt.id,
                                                !qt.isObsolete
                                              );
                                            }}
                                            className="mr-2"
                                            disabled={editingTimeId === qt.id}
                                          />
                                          {editingTimeId === qt.id ? (
                                            <div className="flex-1 flex flex-col gap-2">
                                              <div className="flex gap-2">
                                                <input
                                                  type="number"
                                                  placeholder="Time in seconds"
                                                  value={editingTime}
                                                  onChange={(e) =>
                                                    setEditingTime(
                                                      e.target.value
                                                        ? Number(e.target.value)
                                                        : ""
                                                    )
                                                  }
                                                  className="flex-1 rounded-md border-gray-300 p-2 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600 text-xs"
                                                />
                                                <input
                                                  type="number"
                                                  min="0"
                                                  placeholder="Display time (optional)"
                                                  value={
                                                    editingQuestionDisplayTime
                                                  }
                                                  onChange={(e) =>
                                                    setEditingQuestionDisplayTime(
                                                      e.target.value
                                                        ? Number(e.target.value)
                                                        : ""
                                                    )
                                                  }
                                                  className="flex-1 rounded-md border-gray-300 p-2 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600 text-xs"
                                                />

                                                <div className="flex gap-2">
                                                  <button
                                                    onClick={() =>
                                                      saveEditingTime(qt.id)
                                                    }
                                                    className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 text-xs"
                                                  >
                                                    Save
                                                  </button>
                                                  <button
                                                    onClick={cancelEditingTime}
                                                    className="px-3 py-1 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-xs"
                                                  >
                                                    Cancel
                                                  </button>
                                                </div>
                                              </div>
                                            </div>
                                          ) : (
                                            <div className="flex-1 flex items-center justify-between">
                                              <span
                                                className={
                                                  qt.isObsolete
                                                    ? "line-through text-gray-500 dark:text-gray-400"
                                                    : "text-gray-900 dark:text-gray-400"
                                                }
                                              >
                                                {qt.time +
                                                  " Seconds | Difficulty : " +
                                                  qt.accuracy +
                                                  "%" +
                                                  (qt.questionDisplayTime !==
                                                    null &&
                                                    qt.questionDisplayTime !==
                                                    undefined
                                                    ? ` | Question Display Time: ${qt.questionDisplayTime}s`
                                                    : "")}
                                              </span>
                                              {!qt.id.includes("new-") && (
                                                <button
                                                  onClick={() => {
                                                    startEditingTime(qt);
                                                    setShowNewTimeInput(false);
                                                  }}
                                                  className="ml-2 px-2 py-1 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-xs"
                                                >
                                                  Edit
                                                </button>
                                              )}
                                            </div>
                                          )}
                                        </li>
                                      ))}
                                    </ul>

                                    {!showNewTimeInput ? (
                                      <button
                                        onClick={() => {
                                          setShowNewTimeInput(true);
                                          setEditingTimeId("");
                                          setEditingTime("");
                                          setEditingQuestionDisplayTime("");
                                        }}
                                        className="mt-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-xs"
                                      >
                                        Add New Time
                                      </button>
                                    ) : (
                                      <div className="mt-4 space-y-2">
                                        <div className="flex space-x-2">
                                          <input
                                            type="number"
                                            placeholder="Enter time in seconds"
                                            value={newTime}
                                            onChange={(e) =>
                                              setNewTime(
                                                e.target.value
                                                  ? Number(e.target.value)
                                                  : ""
                                              )
                                            }
                                            className="flex-1 rounded-md border-gray-300 p-2 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600 text-xs"
                                          />
                                          <input
                                            type="number"
                                            min="0"
                                            placeholder="Question display time in seconds (optional)"
                                            value={newQuestionDisplayTime}
                                            onChange={(e) =>
                                              setNewQuestionDisplayTime(
                                                e.target.value
                                                  ? Number(e.target.value)
                                                  : ""
                                              )
                                            }
                                            className="flex-1 rounded-md border-gray-300 p-2 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600 text-xs"
                                          />
                                          <button
                                            onClick={() => {
                                              if (newTime) {
                                                setQuestionTimes((prev) => {
                                                  if (
                                                    prev.some(
                                                      (qt) => qt.time === newTime
                                                    )
                                                  ) {
                                                    return prev;
                                                  }

                                                  return [
                                                    ...prev,
                                                    {
                                                      id:
                                                        "new-" +
                                                        Date.now().toString(),
                                                      time: newTime,
                                                      accuracy: 50,
                                                      isObsolete: false,
                                                      questionDisplayTime:
                                                        newQuestionDisplayTime !==
                                                          ""
                                                          ? newQuestionDisplayTime
                                                          : undefined,
                                                    },
                                                  ];
                                                });
                                                setNewTime("");
                                                setNewQuestionDisplayTime("");
                                                setShowNewTimeInput(false);
                                              }
                                            }}
                                            className="px-4 py-2 text-xs bg-green-600 text-white rounded-md hover:bg-green-700"
                                            disabled={!newTime}
                                          >
                                            Add
                                          </button>
                                          <button
                                            onClick={() => {
                                              setShowNewTimeInput(false);
                                            }}
                                            className="px-3 py-1 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-xs"
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                        <small className="text-gray-500 dark:text-gray-400 text-xs">
                                          Time in seconds to show question text
                                          before displaying options. Leave empty
                                          to show question and options together.
                                        </small>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {selectedQType.title === 'SQL' && (
                                <>
                                  <div className="flex flex-col items-start py-2 lg:flex-row ">
                                    <div className=" text-sm font-medium text-gray-700 w-64 text-left  dark:text-gray-400 flex flex-col ">
                                      <label className="text-xs">
                                        Database Schema Setup Script
                                      </label>
                                    </div>
                                    <textarea
                                      className="w-full rounded-md border-gray-300 border p-1 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600 h-24"
                                      value={setupScript}
                                      onChange={(e) => setSetupScript(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === " " || e.code === "Space") {
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }
                                      }}
                                      placeholder="CREATE TABLE Employee..."
                                    />
                                  </div>
                                  <div className="flex flex-col items-start py-2 lg:flex-row ">
                                    <div className=" text-sm font-medium text-gray-700 w-64 text-left  dark:text-gray-400 flex flex-col ">
                                      <label className="text-xs">
                                        Expected SQL Query
                                      </label>
                                    </div>
                                    <textarea
                                      className="w-full rounded-md border-gray-300 border p-1 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600 h-24"
                                      value={expectedQuery}
                                      onChange={(e) => setExpectedQuery(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === " " || e.code === "Space") {
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }
                                      }}
                                      placeholder="SELECT * FROM Employee..."
                                    />
                                  </div>
                                  <div className="flex justify-end py-2 w-full">
                                    <button
                                      type="button"
                                      disabled={isTesting}
                                      onClick={() => handleValidateCode('SQL')}
                                      className="rounded-md bg-indigo-100 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      {isTesting ? "Testing..." : "Validate SQL"}
                                    </button>
                                  </div>
                                </>
                              )}

                              {selectedQType.title === 'JAVASCRIPT' && (
                                <>
                                  <div className="flex flex-col items-start py-2 lg:flex-row ">
                                    <div className=" text-sm font-medium text-gray-700 w-64 text-left  dark:text-gray-400 flex flex-col ">
                                      <label className="text-xs">
                                        Initial Code (Boilerplate)
                                      </label>
                                    </div>
                                    <textarea
                                      className="w-full rounded-md border-gray-300 border p-1 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600 h-24"
                                      value={initialCode}
                                      onChange={(e) => setInitialCode(e.target.value)}
                                      placeholder="function solution(a, b) {&#10;  // Write code here&#10;}"
                                    />
                                  </div>
                                  <div className="flex flex-col items-start py-2 lg:flex-row ">
                                    <div className=" text-sm font-medium text-gray-700 w-64 text-left  dark:text-gray-400 flex flex-col ">
                                      <label className="text-xs">
                                        Test Cases (JSON Array)
                                      </label>
                                    </div>
                                    <textarea
                                      className="w-full rounded-md border-gray-300 border p-1 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600 h-32 font-mono text-xs"
                                      value={testCases}
                                      onChange={(e) => setTestCases(e.target.value)}
                                      placeholder='[{"input": "1, 2", "expectedOutput": "3", "isHidden": false}]'
                                    />
                                  </div>
                                  <div className="flex flex-col items-start py-2 lg:flex-row ">
                                    <div className=" text-sm font-medium text-gray-700 w-64 text-left  dark:text-gray-400 flex flex-col ">
                                      <label className="text-xs">
                                        Expected Solution (For Test Validation)
                                      </label>
                                    </div>
                                    <textarea
                                      className="w-full rounded-md border-gray-300 border p-1 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600 h-24"
                                      value={expectedQuery}
                                      onChange={(e) => setExpectedQuery(e.target.value)}
                                      placeholder="function solution(a, b) {&#10;  return a + b;&#10;}"
                                    />
                                  </div>
                                  <div className="flex justify-end py-2 w-full">
                                    <button
                                      type="button"
                                      disabled={isTesting}
                                      onClick={() => handleValidateCode('JAVASCRIPT')}
                                      className="rounded-md bg-indigo-100 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      {isTesting ? "Testing..." : "Validate JS"}
                                    </button>
                                  </div>
                                </>
                              )}

                              {selectedQType.title === 'REACT' && (
                                <>
                                  <div className="flex flex-col items-start py-2 lg:flex-row ">
                                    <div className=" text-sm font-medium text-gray-700 w-64 text-left  dark:text-gray-400 flex flex-col ">
                                      <label className="text-xs">
                                        Component Template
                                      </label>
                                    </div>
                                    <textarea
                                      className="w-full rounded-md border-gray-300 border p-1 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600 h-24 font-mono text-xs"
                                      value={initialCode}
                                      onChange={(e) => setInitialCode(e.target.value)}
                                      placeholder="export default function MyComponent() {&#10;  return <div>Hello World</div>;&#10;}"
                                    />
                                  </div>
                                  <div className="flex flex-col items-start py-2 lg:flex-row ">
                                    <div className=" text-sm font-medium text-gray-700 w-64 text-left  dark:text-gray-400 flex flex-col ">
                                      <label className="text-xs">
                                        Test Cases (JSON Array)
                                      </label>
                                    </div>
                                    <textarea
                                      className="w-full rounded-md border-gray-300 border p-1 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600 h-32 font-mono text-xs"
                                      value={testCases}
                                      onChange={(e) => setTestCases(e.target.value)}
                                      placeholder='[{"input": "{}", "expectedOutput": "<div>Hello World</div>", "isHidden": false}]'
                                    />
                                  </div>
                                  <div className="flex flex-col items-start py-2 lg:flex-row ">
                                    <div className=" text-sm font-medium text-gray-700 w-64 text-left  dark:text-gray-400 flex flex-col ">
                                      <label className="text-xs">
                                        Expected Solution (For Test Validation)
                                      </label>
                                    </div>
                                    <textarea
                                      className="w-full rounded-md border-gray-300 border p-1 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600 h-24"
                                      value={expectedQuery}
                                      onChange={(e) => setExpectedQuery(e.target.value)}
                                      placeholder="export default function MyComponent() {&#10;  return <div>Hello World</div>;&#10;}"
                                    />
                                  </div>
                                  <div className="flex justify-end py-2 w-full">
                                    <button
                                      type="button"
                                      disabled={isTesting}
                                      onClick={() => handleValidateCode('REACT')}
                                      className="rounded-md bg-indigo-100 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      {isTesting ? "Testing..." : "Validate React"}
                                    </button>
                                  </div>
                                </>
                              )}

                              <div className="flex flex-col items-start py-2 lg:flex-row ">
                                <div className=" text-sm font-medium text-gray-700 w-64 text-left  dark:text-gray-400 flex flex-col ">
                                  <label
                                    htmlFor="questionTags"
                                    className="text-xs"
                                  >
                                    Tags{" "}
                                    <span className="text-red-700 ">*</span>
                                  </label>
                                  <label
                                    htmlFor="questionTags"
                                    className="text-[10px]"
                                  >
                                    (Seperated By commas)
                                  </label>
                                </div>
                                <input
                                  id="questionTags"
                                  name="questionTags"
                                  type="text"
                                  className="w-full rounded-md border-gray-300 border p-1 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600"
                                  onChange={(e) => setTags(e.target.value)}
                                  value={tags}
                                />
                              </div>
                            </div>
                          )}

                          {pageNumber === 2 &&
                            shouldShowOptions && (
                              <>
                                <div className="flex flex-col items-start py-2 lg:flex-row ">
                                  <CheckboxInput
                                    selectedQType={selectedQType}
                                    getOptionsfromChild={getOptionsfromChild}
                                    selectedOptions={options}
                                    isQuestionsModalOpen={open}
                                    singleSelect={roleUsesOptions ? roleOptionMode === "single" : undefined}
                                  />
                                </div>
                              </>
                            )}
                        </form>
                      </div>
                      <div className="mt-auto p-4 ">
                        <div className="flex justify-between">
                          {pageNumber === 1 ? (
                            <button
                              onClick={() => isModalClosed()}
                              type="submit"
                              className={` rounded-md px-4 py-2 text-sm font-semibold shadow-sm focus:outline-none focus:ring focus:border-indigo-500 bg-gray-300 text-gray-500  hover:bg-red-500 hover:text-white`}
                            >
                              Cancel
                            </button>
                          ) : (
                            <button
                              onClick={() => setPageNumber(1)}
                              type="submit"
                              className={`flex flex-row gap-2 items-center rounded-md px-4 py-2 text-sm font-semibold shadow-sm focus:outline-none focus:ring focus:border-indigo-500 bg-gray-300 text-gray-500 hover:bg-gray-500 hover:text-white`}
                            >
                              <ArrowUturnLeftIcon height={15} />
                              Back
                            </button>
                          )}
                          {selectedQuestionId ? (
                            <div className="gap-2 flex flex-row">
                              {(selectedQType.title === "SINGLE" ||
                                selectedQType.title === "MULTIPLE" ||
                                selectedQType.title === "IMAGE" ||
                                roleUsesOptions) &&
                                pageNumber === 1 && (
                                  <button
                                    onClick={() => setPageNumber(2)}
                                    type="submit"
                                    className={`rounded-md px-4 py-2 text-sm font-semibold shadow-sm focus:outline-none focus:ring focus:border-indigo-500 ${"bg-indigo-600 text-white hover:bg-indigo-500 cursor-pointer"}`}
                                  >
                                    Edit Options
                                  </button>
                                )}

                              <button
                                onClick={handleQuestionUpdate}
                                type="submit"
                                className={`rounded-md px-4 py-2 text-sm font-semibold shadow-sm focus:outline-none focus:ring focus:border-indigo-500 ${!isDisabled
                                  ? "bg-indigo-600 text-white hover:bg-indigo-500 cursor-pointer"
                                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
                                  }`}
                                disabled={
                                  isDisabled || isParentQuestionMissing
                                }
                              >
                                Update
                              </button>
                            </div>
                          ) : (
                            <div>
                              {pageNumber === 1 &&
                                (selectedQType.title === "SINGLE" ||
                                  selectedQType.title === "MULTIPLE" ||
                                  selectedQType.title === "IMAGE" ||
                                  roleUsesOptions) ? (
                                <button
                                  onClick={() => setPageNumber(2)}
                                  type="submit"
                                  className={`rounded-md px-4 py-2 text-sm font-semibold shadow-sm focus:outline-none focus:ring focus:border-indigo-500 ${"bg-indigo-600 text-white hover:bg-indigo-500 cursor-pointer"}`}
                                >
                                  Select Options
                                </button>
                              ) : (
                                <button
                                  onClick={handleQuestionSubmit}
                                  type="submit"
                                  className={`rounded-md px-4 py-2 text-sm font-semibold shadow-sm focus:outline-none focus:ring focus:border-indigo-500 ${!isDisabled
                                    ? "bg-indigo-600 text-white hover:bg-indigo-500 cursor-pointer"
                                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                                    }`}
                                  disabled={
                                    isDisabled
                                  }
                                >
                                  Submit
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}
                {loading && <Loading />}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
